/**
 * The email ask, in one implementation, for every product.
 *
 * ## THIS FILE IS THE KIT. Do not edit the copy in a product.
 *
 * ## The rule it enforces, which is a design decision rather than a growth tactic
 *
 * **The first result is always free. The ask comes after, is one field, and asks for something
 * whose value the visitor has just personally seen.**
 *
 * Gating the first thing costs an order of magnitude in reach, and a single field after an
 * interactive result converts several times better than a form in front of one. That is why the
 * rule lives in the component rather than in a document: `<LeadInline>` can only be rendered
 * inside a result, and `<LeadModal>` refuses to open on a `result` trigger it was not given a
 * subject for.
 *
 * ## Why a modal AND an inline form
 *
 * They are different moments and the same intake.
 *
 * `<LeadInline>` is the post-result ask — one field, in the flow, where the visitor is already
 * looking. It converts best and it is the default.
 *
 * `<LeadModal>` is for the asks that are not attached to a result: a nav CTA, a pricing page, a
 * "talk to us" that should not navigate away from what somebody was reading. A modal that
 * interrupts a first visit is the thing this is NOT for, and `trigger` records which it was so
 * the difference is measurable rather than assumed.
 *
 * ## Why it takes its colour from the host product
 *
 * Every visual value here is a CSS custom property both design systems already publish —
 * `--accent`, `--ink`, `--rule`, the spacing scale. The kit ships behaviour and structure; a kit
 * that shipped a palette would be a kit each product forks the first time a designer looks at it,
 * and a fork is how `analytics.ts` became two files that disagreed about which continent the data
 * lived on.
 *
 * ## What it posts
 *
 * One intake, always authoxi's leads plane: it owns the portfolio's `leads` table and the only
 * mail path in the group. `product`, `source`, `subject` and `trigger` ride on every submission,
 * because a table that records only that somebody arrived answers nothing anyone would want to
 * know.
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { trackLeadFailed, trackLeadShown, trackLeadSubmitted } from "./events";

// ── the contract with the host product ───────────────────────────────────────────────────

/**
 * How the kit reaches the network and the product's own copy.
 *
 * An interface rather than an import, because the two products' HTTP clients differ in ways that
 * are correct for each — authoxi's console is same-origin with its API, agitome's is not — and a
 * kit that insisted on one would be a kit that only fits one.
 */
export interface LeadTransport {
  /** POST a lead. Resolves with whatever the leads plane returns; rejects on any failure. */
  submit(body: LeadBody): Promise<{ unsubscribe_url?: string } | null>;
}

export interface LeadBody {
  email: string;
  /** The closed portfolio taxonomy: `authoxi | agentlox | agitome | agiremit`. */
  product: string;
  /** Which surface. A closed set per product — the field that answers "which surface converts". */
  source: string;
  /** What they were looking at: the host graded, the incident, the report id. */
  subject?: string;
  company?: string;
  note?: string;
}

export interface LeadCopy {
  title: string;
  body: string;
  cta: string;
  /** Shown after a successful submission. Says what happens next, in the product's own voice. */
  done?: string;
}

export type LeadTrigger = "result" | "cta" | "page";

export interface LeadProps {
  transport: LeadTransport;
  product: string;
  source: string;
  copy: LeadCopy;
  subject?: string;
  /**
   * `qualified` adds company and a note. Use it where somebody has already decided to talk to
   * you — a design-partner page, a contact form — and never after a tool result, where an extra
   * field is the difference between an address and a bounce.
   */
  variant?: "one-field" | "qualified";
  trigger?: LeadTrigger;
}

// ── the shared form, which is the whole implementation ───────────────────────────────────

function useLeadForm({ transport, product, source, subject, variant, trigger }: LeadProps) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [note, setNote] = useState("");
  const [done, setDone] = useState<{ unsubscribe_url?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        const result = await transport.submit({
          email,
          product,
          source,
          subject,
          company: variant === "qualified" && company ? company : undefined,
          note: variant === "qualified" && note ? note : undefined,
        });
        setDone(result ?? {});
        trackLeadSubmitted(source, { subject, trigger, variant });
      } catch (err) {
        // NEVER LOSE WHAT THEY TYPED. Re-typing an address after a failure is where people decide
        // it was not worth it — so the fields keep their values and only the error is added.
        setError("That did not go through. Try again?");
        // Counted as well as shown. A form that fails quietly is how you lose the leads you were
        // counting, and this product has done exactly that before: a form posted to a route that
        // did not exist, fell back to a mailto nobody sent, silently, for weeks.
        trackLeadFailed(source, err instanceof Error ? err.name : "unknown");
      } finally {
        setBusy(false);
      }
    },
    [busy, transport, email, product, source, subject, variant, company, note, trigger],
  );

  return { email, setEmail, company, setCompany, note, setNote, done, busy, error, onSubmit };
}

function LeadFields({
  form,
  copy,
  variant,
  autoFocus,
}: {
  form: ReturnType<typeof useLeadForm>;
  copy: LeadCopy;
  variant: "one-field" | "qualified";
  autoFocus?: boolean;
}) {
  const id = useId();

  if (form.done) {
    return (
      <div className="leadgate leadgate--done" data-testid="lead-done">
        <p className="leadgate__done">{copy.done ?? `We will write to ${form.email} — and nothing else will.`}</p>
        {/* RENDERED, not merely returned. An unsubscribe link the sender keeps to itself is not a
            way out, and it is the one promise every ask in the portfolio makes. */}
        {form.done.unsubscribe_url ? (
          <p className="leadgate__fine">
            <a href={form.done.unsubscribe_url}>Leave in one click</a> — no account, immediate.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <form className="leadgate__form" onSubmit={form.onSubmit} data-testid="lead-form">
      <label className="leadgate__label" htmlFor={`${id}-email`}>
        Email address
      </label>
      <input
        id={`${id}-email`}
        className="leadgate__field"
        type="email"
        required
        autoComplete="email"
        autoFocus={autoFocus}
        placeholder="you@company.com"
        data-testid="lead-email"
        value={form.email}
        onChange={(e) => form.setEmail(e.target.value)}
      />

      {variant === "qualified" ? (
        <>
          <input
            className="leadgate__field"
            type="text"
            placeholder="Company (optional)"
            aria-label="Company"
            data-testid="lead-company"
            value={form.company}
            onChange={(e) => form.setCompany(e.target.value)}
          />
          {/* The note is the point of the qualified variant. An address with no sentence attached
              is a lead you have to open a conversation to qualify, which at this size means you
              answer it three days later. */}
          <textarea
            className="leadgate__field leadgate__note"
            rows={3}
            placeholder="What are you trying to do?"
            aria-label="Your message"
            data-testid="lead-note"
            value={form.note}
            onChange={(e) => form.setNote(e.target.value)}
          />
        </>
      ) : null}

      <button className="leadgate__submit" type="submit" disabled={form.busy} data-testid="lead-submit">
        {form.busy ? "…" : copy.cta}
      </button>

      {form.error ? (
        <p className="leadgate__error" role="alert" data-testid="lead-error">
          {form.error}
        </p>
      ) : null}

      {/* Said BEFORE the field is filled, not after it is submitted. A promise about what happens
          to an address is worth nothing once the address has been handed over. */}
      <p className="leadgate__fine">One field. One click to leave. Never sold.</p>
    </form>
  );
}

// ── inline: the post-result ask, and the default ─────────────────────────────────────────

/**
 * The ask, in the flow, after a result.
 *
 * Render it INSIDE a result and nowhere else. That placement is the rule — zero asks before a
 * result, exactly one after — and it is enforced by where this is rendered rather than by a
 * comment, which is why it takes `subject`: the thing whose value the visitor has just seen.
 */
export function LeadInline(props: LeadProps) {
  const form = useLeadForm({ ...props, trigger: props.trigger ?? "result" });

  // Fired where it RENDERS, which is the only place that knows it was actually seen. Without it a
  // conversion rate has no denominator — which is the difference between "which surface converts"
  // and "which surface produced the most addresses", questions with different answers.
  useEffect(() => {
    trackLeadShown(props.source, props.trigger ?? "result", props.subject);
  }, [props.source, props.trigger, props.subject]);

  return (
    <div className="leadgate" data-testid="lead-inline">
      <p className="leadgate__title">{props.copy.title}</p>
      <p className="leadgate__body">{props.copy.body}</p>
      <LeadFields form={form} copy={props.copy} variant={props.variant ?? "one-field"} />
    </div>
  );
}

// ── modal: for the asks that are not attached to a result ────────────────────────────────

/**
 * The same ask, in a dialog, for a CTA that should not navigate away.
 *
 * ACCESSIBILITY IS NOT OPTIONAL HERE, because a modal is the one pattern that breaks a keyboard
 * user completely when it is done casually: focus moves in on open and returns to the trigger on
 * close, Escape closes, the backdrop closes, and focus is trapped in between. A dialog somebody
 * can tab out of is a page with an invisible overlay.
 */
export function LeadModal({
  open,
  onClose,
  ...props
}: LeadProps & { open: boolean; onClose: () => void }) {
  const form = useLeadForm({ ...props, trigger: props.trigger ?? "cta" });
  const panel = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<Element | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    trackLeadShown(props.source, props.trigger ?? "cta", props.subject);

    restoreTo.current = document.activeElement;
    // The first focusable thing in the panel, which is the email field.
    panel.current?.querySelector<HTMLElement>("input, button, textarea")?.focus();

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panel.current) return;
      const focusable = panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // Both, not just the length: `noUncheckedIndexedAccess` is right that an index into a
      // NodeList is an assumption, and a trap that silently does nothing is worse than one that
      // never installed.
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKey);
    // The page behind a dialog must not scroll; a modal you can scroll past is a modal that
    // reads as a rendering bug.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      (restoreTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open, onClose, props.source, props.trigger, props.subject]);

  if (!open) return null;

  return (
    <div
      className="leadgate__backdrop"
      data-testid="lead-modal"
      onClick={(e) => {
        // Only the backdrop itself. A click that started inside the panel and ended on the
        // backdrop — a drag while selecting text — must not close the dialog.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="leadgate leadgate--modal"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          className="leadgate__close"
          type="button"
          aria-label="Close"
          data-testid="lead-close"
          onClick={onClose}
        >
          ×
        </button>
        <p className="leadgate__title" id={titleId}>
          {props.copy.title}
        </p>
        <p className="leadgate__body">{props.copy.body}</p>
        <LeadFields form={form} copy={props.copy} variant={props.variant ?? "one-field"} autoFocus />
      </div>
    </div>
  );
}

/**
 * Open the modal from anywhere, without every page owning a piece of state.
 *
 *     const lead = useLeadModal()
 *     <button onClick={() => lead.open({ source: "pricing", subject: "team" })}>Talk to us</button>
 *     <lead.Modal transport={t} product="authoxi" copy={COPY.pricing} />
 */
export function useLeadModal() {
  const [state, setState] = useState<{ open: boolean; source?: string; subject?: string }>({
    open: false,
  });

  const open = useCallback(
    (at: { source: string; subject?: string }) => setState({ open: true, ...at }),
    [],
  );
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  const Modal = useCallback(
    (props: Omit<LeadProps, "source"> & { source?: string }) => (
      <LeadModal
        {...props}
        source={state.source ?? props.source ?? "unknown"}
        subject={state.subject ?? props.subject}
        open={state.open}
        onClose={close}
      />
    ),
    [state, close],
  );

  return { open, close, Modal, isOpen: state.open } as const;
}

/** Convenience for a product that wants the CTA and the dialog as one element. */
export function LeadCta({
  children,
  onOpen,
  className = "leadgate__cta",
}: {
  children: ReactNode;
  onOpen: () => void;
  className?: string;
}) {
  return (
    <button type="button" className={className} onClick={onOpen}>
      {children}
    </button>
  );
}
